import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Building2, MapPin, BadgeCheck, ShieldCheck, Globe, Phone, FileText, ArrowLeft, Handshake } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import { TrustStars, FreshlyVerifiedBadge } from "@/pages/Directory";

export default function CompanyProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    api.get(`/directory/${id}`).then((r) => setCompany(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await api.post(`/directory/${id}/claim`);
      setClaimed(true);
      toast.success(r.data.message || "Cerere trimisă.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setClaiming(false); }
  };

  if (notFound) return (
    <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="company-not-found">
      <p className="text-slate-500">Compania nu a fost găsită.</p>
      <Link to="/directory" className="inline-block mt-4 text-orange-600 font-semibold hover:underline">Înapoi la cégjegyzék</Link>
    </div>
  );
  if (!company) return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-slate-400">Se încarcă...</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="company-profile-page">
      <Link to="/directory" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-orange-600 mb-6 transition-colors" data-testid="back-to-directory">
        <ArrowLeft className="w-4 h-4" /> Cégjegyzék
      </Link>

      {/* Header */}
      <div className="border border-slate-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row gap-6 sm:items-center">
        <div className="w-20 h-20 bg-slate-900 text-white flex items-center justify-center shrink-0"><Building2 className="w-10 h-10" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {company.oxo_endorsed && <span className="inline-flex items-center gap-1 bg-amber-500 text-slate-900 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" data-testid="profile-endorsed"><BadgeCheck className="w-3 h-3" /> OXO Endorsed</span>}
            {company.freshly_verified && <FreshlyVerifiedBadge month={company.verified_month} />}
            {company.verified && !company.freshly_verified && <span className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" data-testid="profile-verified"><ShieldCheck className="w-3 h-3" /> Verificat</span>}
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">{company.company_name}</h1>
          {company.niche && <div className="text-[11px] uppercase tracking-wide font-bold text-orange-600 mt-1">{company.niche}</div>}
          <div className="flex items-center gap-3 mt-3">
            <TrustStars rating={company.trust_rating} size="w-5 h-5" />
            <span className="text-sm text-slate-500">{company.trust_rating}/5 · Trust Rating</span>
          </div>
        </div>
        {user && !company.claimed && (
          <button onClick={claim} disabled={claiming || claimed}
            className="inline-flex items-center gap-2 border border-slate-900 hover:bg-slate-900 hover:text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-50 shrink-0 self-start"
            data-testid="claim-profile-btn">
            <Handshake className="w-4 h-4" /> {claimed ? "Cerere trimisă" : "Revendică profilul"}
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8 mt-8">
        {/* Info */}
        <aside className="space-y-4" data-testid="company-info">
          <div className="border border-slate-200 p-5 space-y-3 text-sm">
            {(company.city || company.county) && <div className="flex items-start gap-2.5"><MapPin className="w-4 h-4 text-slate-400 mt-0.5" /> <span>{[company.city, company.county].filter(Boolean).join(", ")}</span></div>}
            {company.cui && <div className="flex items-start gap-2.5"><FileText className="w-4 h-4 text-slate-400 mt-0.5" /> <span>CUI: {company.cui}</span></div>}
            {company.phone && <div className="flex items-start gap-2.5"><Phone className="w-4 h-4 text-slate-400 mt-0.5" /> <a href={`tel:${company.phone}`} className="hover:text-orange-600">{company.phone}</a></div>}
            {company.website && <div className="flex items-start gap-2.5"><Globe className="w-4 h-4 text-slate-400 mt-0.5" /> <a href={company.website} target="_blank" rel="noopener noreferrer" className="hover:text-orange-600 break-all">{company.website.replace(/^https?:\/\//, "")}</a></div>}
          </div>
          {company.description && (
            <div className="border border-slate-200 p-5">
              <h3 className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-2">Despre companie</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{company.description}</p>
            </div>
          )}
        </aside>

        {/* Listings */}
        <div>
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-5">Oferte active ({company.listings.length})</h2>
          {company.listings.length === 0 ? (
            <div className="border border-slate-200 p-12 text-center" data-testid="no-company-listings">
              <p className="text-slate-500">Această companie nu are oferte active momentan.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="company-listings">
              {company.listings.map((l, i) => <ProductCard key={l.id} product={l} priority={i < 4} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
